import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { env } from '@/config/env';
import { loadBranding, type PlatformBranding } from '@/services/appearance';

/**
 * A MARCA DA PLATAFORMA, CARREGADA UMA VEZ.
 *
 * Store, e não uma busca em cada componente, por dois motivos:
 *
 * 1. O logo aparece na barra lateral, na tela de entrada e no funil. Três
 *    consultas ao abrir a página seriam três esperas para desenhar a
 *    mesma imagem.
 * 2. Enquanto não carregou, a tela precisa mostrar ALGO. Um estado
 *    compartilhado deixa todos os pontos concordarem sobre o que exibir,
 *    em vez de um piscar o logo padrão e outro o customizado.
 *
 * Funciona SEM sessão: a tela de entrada precisa do logo antes de existir
 * login, e a policy de leitura é pública justamente por isso.
 */
export const useBrandingStore = defineStore('branding', () => {
  const branding = ref<PlatformBranding>({
    appName: null,
    logoUrl: null,
    loginLogoUrl: null,
    faviconUrl: null,
  });

  const carregado = ref(false);
  let emVoo: Promise<void> | null = null;

  /**
   * Nome exibido.
   *
   * O padrão vem de `env`, e não de um literal aqui: existiam dois
   * — 'Assessor' nesta linha e outro em `config/env.ts` — e dois padrões
   * para a mesma coisa divergem no dia em que alguém troca só um.
   *
   * A ordem é: o que o dono da plataforma gravou, senão o do build.
   */
  const appName = computed(() => branding.value.appName || env.appName);
  const logoUrl = computed(() => branding.value.logoUrl);

  /**
   * Logo da entrada, com queda para o do painel.
   *
   * Quem sobe um logo e não sobe o da entrada quase sempre quer o mesmo
   * nos dois lugares — obrigar a subir duas vezes é atrito sem ganho.
   */
  const loginLogoUrl = computed(() => branding.value.loginLogoUrl || branding.value.logoUrl);

  async function initialize(): Promise<void> {
    if (carregado.value) return;
    // Uma requisição só, mesmo com vários componentes pedindo ao mesmo
    // tempo no primeiro render.
    if (emVoo) return emVoo;

    emVoo = (async () => {
      try {
        branding.value = await loadBranding();
        aplicarFavicon(branding.value.faviconUrl);
      } catch {
        // Marca é aparência. Falhar aqui não pode impedir a pessoa de
        // entrar — as telas caem no logo padrão e a vida segue.
      } finally {
        carregado.value = true;
        emVoo = null;
      }
    })();

    return emVoo;
  }

  /** Recarrega depois de um upload, para a mudança aparecer sem F5. */
  async function refresh(): Promise<void> {
    carregado.value = false;
    await initialize();
  }

  return { branding, carregado, appName, logoUrl, loginLogoUrl, initialize, refresh };
});

/**
 * Troca o ícone da aba.
 *
 * Reaproveita a tag existente em vez de criar outra: dois `<link
 * rel="icon">` deixam o navegador escolher, e a escolha varia entre
 * navegadores — o ícone ficaria certo em um e errado em outro.
 */
function aplicarFavicon(url: string | null): void {
  if (!url) return;

  let tag = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
  if (!tag) {
    tag = document.createElement('link');
    tag.rel = 'icon';
    document.head.appendChild(tag);
  }
  tag.href = url;
}
